import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddResourceModal = ({ categoryId, categories, onClose, onSave, editResource }) => {
  const [formData, setFormData] = useState({
    name: '',
    identifier: '',
    category: categoryId || '',
    status: 'Available',
    description: '',
    hourlyRate: '',
    dailyRate: '',
    specifications: {}
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editResource) {
      setFormData({
        name: editResource?.name || '',
        identifier: editResource?.identifier || '',
        category: editResource?.categoryId || editResource?.category || '',
        status: editResource?.status || 'Available',
        description: editResource?.description || '',
        hourlyRate: editResource?.hourlyRate != null ? String(editResource?.hourlyRate) : '',
        dailyRate: editResource?.dailyRate != null ? String(editResource?.dailyRate) : '',
        specifications: editResource?.specifications || {}
      });
    }
  }, [editResource]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Resource name is required';
    }
    if (!formData?.category) {
      newErrors.category = 'Category is required';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const toTitleCase = (str) =>
        str?.trim()?.replace(/\w\S*/g, (word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase());
      const saveData = {
        ...formData,
        name: toTitleCase(formData?.name),
        hourlyRate: formData?.hourlyRate ? parseFloat(formData?.hourlyRate) || null : null,
        dailyRate: formData?.dailyRate ? parseFloat(formData?.dailyRate) || null : null,
        description: formData?.description?.trim() || null,
      };
      await onSave(saveData);
    } catch (err) {
      alert('Failed to save resource: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryOptions = categories?.map(cat => ({
    value: cat?.id,
    label: cat?.name
  }));

  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'Maintenance', label: 'Maintenance' },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {editResource ? 'Edit Resource' : 'Add New Resource'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-4">
            <Input
              label="Resource Name"
              type="text"
              placeholder="Enter resource name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              error={errors?.name}
              required
            />

            <Input
              label="Identifier/ID"
              type="text"
              placeholder="Enter identifier (optional)"
              value={formData?.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e?.target?.value })}
              description="Unique identifier for tracking"
            />

            <Select
              label="Category"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => setFormData({ ...formData, category: value })}
              error={errors?.category}
              required
            />

            <Select
              label="Status"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => setFormData({ ...formData, status: value })}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Description</label>
              <textarea
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                rows={3}
                placeholder="Enter resource description (optional)"
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Hourly Rate ($)"
                type="number"
                placeholder="0.00"
                value={formData?.hourlyRate}
                onChange={(e) => setFormData({ ...formData, hourlyRate: e?.target?.value })}
                min="0"
                step="0.01"
              />
              <Input
                label="Daily Rate ($)"
                type="number"
                placeholder="0.00"
                value={formData?.dailyRate}
                onChange={(e) => setFormData({ ...formData, dailyRate: e?.target?.value })}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-4 md:px-6 py-4 border-t border-border flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            iconName={editResource ? 'Save' : 'Plus'} 
            iconPosition="left" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (editResource ? 'Save Changes' : 'Add Resource')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddResourceModal;